import React from 'react';
import { AGE_UNITS, parseAgeValue, serializeAgeValue, clampAgeAmount } from '../../utils/ageValue';

/**
 * Age number input + Years/Months/Days unit selector, sharing one
 * amount+unit control so RegisterVisitForm and BookAppointmentForm don't
 * each re-implement the same digit-stripping <input> (which is what made
 * infant ages unrepresentable in the first place -- see ageValue.js for
 * the stored-string convention this composes/decomposes).
 *
 * Controlled the same way a plain <input> would be: `value` is the raw
 * stored string ("6m", "34", ""), `onChange` receives the next raw string.
 */
// `inputStyle` is for inline-styled consumers (RegisterVisitForm). Tailwind
// consumers pass `inputClassName` instead — an inline style object cannot
// express `dark:` variants, so styling a Tailwind form through `inputStyle`
// would look correct in light mode and wrong in dark. With NEITHER supplied
// the control renders borderless and effectively invisible on a white card,
// which is exactly what happened on the admit-desk register dialog.
export default function AgeInput({
  value, onChange, inputStyle, inputClassName, placeholder = 'e.g. 35', disabled,
}) {
  const { amount, unit } = parseAgeValue(value);

  const handleAmountChange = (raw) => {
    onChange(serializeAgeValue(clampAgeAmount(raw, unit), unit));
  };

  const handleUnitChange = (nextUnit) => {
    onChange(serializeAgeValue(clampAgeAmount(amount, nextUnit), nextUnit));
  };

  const selectStyle = {
    ...inputStyle,
    width: '5rem',
    flex: '0 0 auto',
    paddingLeft: '0.5rem',
    paddingRight: '0.5rem',
  };

  return (
    <div style={{ display: 'flex', gap: '0.375rem' }}>
      <input
        type="text"
        inputMode="numeric"
        value={amount}
        onChange={(e) => handleAmountChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        // The amount box carried no accessible name anywhere it was used —
        // only the unit select did. A screen reader announced an unlabelled
        // text field next to "Age unit". Named here so every consumer gets it.
        aria-label="Age"
        className={inputClassName}
        style={{ ...inputStyle, flex: '1 1 auto', minWidth: 0 }}
      />
      <select
        value={unit}
        onChange={(e) => handleUnitChange(e.target.value)}
        disabled={disabled}
        className={inputClassName}
        style={selectStyle}
        aria-label="Age unit"
      >
        {AGE_UNITS.map((u) => (
          <option key={u.value} value={u.value}>{u.label}</option>
        ))}
      </select>
    </div>
  );
}
